import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "./context/AppContext";
import { api } from "./lib/api";
import { AnnouncementBar, Header } from "./components/Header";
import { Footer, FloatingUtilities } from "./components/Footer";
import { AuthGate, SearchDialog, CartDrawer } from "./components/Overlays";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductPage from "./pages/ProductPage";
import { CollectionsIndex, CollectionPage, StorePage, StoresIndex, NotFound, MessagesPage } from "./pages/Catalog";
import { BulkReview } from "./pages/admin/BulkReview";
import { ForgotPassword, ResetPassword, VerifyEmail } from "./pages/AuthEmail";
import EmailCenter from "./pages/admin/EmailCenter";
import NotificationsAdmin from "./pages/admin/NotificationsAdmin";
import AccountSettings from "./pages/AccountSettings";
import SecurityAdmin from "./pages/admin/SecurityAdmin";
import CompanySettings from "./pages/admin/CompanySettings";
import LaunchReadiness from "./pages/admin/LaunchReadiness";
import AdminSharedCatalog from "./components/SharedCatalog";
import SourceHub from "./pages/admin/SourceHub";
import AdminCardSecurity from "./components/CardSecurity";
import DealsAdmin from "./pages/admin/DealsAdmin";
import { StoresForSale, Wholesale, MyDeals } from "./pages/Deals";
import PolicyPage from "./pages/PolicyPage";
import { Login, AuthCallback, RequireAuth, Account, Wishlist, TrackOrder, AccountSecurity, PasswordGate } from "./pages/AccountPages";
import { About, Drops, Experience, Help } from "./pages/InfoPages";
import { SellerServices, StoreApply } from "./pages/SellerPages";
import ShopsDirectory from "./pages/Shops";
import { AdminLayout, AdminProducts, AdminHomepage, CatalogRequests } from "./pages/admin/AdminPages";
import StudioPage from "./pages/admin/StudioPage";
import ReviewsModeration from "./pages/ReviewsModeration";
import SupportDesk from "./pages/SupportDesk";
import ProductEditor from "./pages/admin/ProductEditor";
import HeroManager from "./pages/admin/HeroManager";
import PaymentsAdmin from "./pages/admin/PaymentsAdmin";
import { CouponsAdmin, StoresAdmin, UsersAdmin } from "./pages/admin/CommerceAdmin";
import { CardsExplore, ClaimCard, CardTerms } from "./pages/Cards";
import { Wallet, MissingDeposit } from "./pages/Wallet";
import { Checkout, OrderPage, WelcomePopup } from "./pages/Checkout";
import { PartnerPortal, InviteAccept } from "./pages/Partner";

import Discover from "./pages/Discover";
import { CategoriesAdmin, I18nAdmin } from "./pages/admin/CatalogI18nAdmin";
import { tx, useLang } from "./lib/i18n";
const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const StagingBadge = () => { useLang();
  const [env, setEnv] = useState(null);
  useEffect(() => { api.get("/site/env").then((r) => setEnv(r.data.env)).catch(() => {}); }, []);
  if (!env || env === "production") return null;
  return <div className="pointer-events-none fixed left-1/2 top-0 z-[60] -translate-x-1/2 rounded-b-md bg-amber-400 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black" data-testid="staging-badge">{tx("Staging · test data only")}</div>;
};

const Shell = () => {
  const [settings, setSettings] = useState(null);
  const { motion } = useApp();
  const lang = useLang();
  useEffect(() => { api.get("/home").then((r) => setSettings(r.data.settings)).catch(() => {}); }, [lang]);
  return (
    <div className="min-h-screen" data-motion={motion}>
      <StagingBadge />
      <AnnouncementBar settings={settings} />
      <Header settings={settings} />
      <main><RetryOutlet /></main>
      <Footer />
      <FloatingUtilities />
      <AuthGate /><SearchDialog /><CartDrawer /><WelcomePopup /><PasswordGate />
    </div>
  );
};

const RetryOutlet = () => {
  const [k, setK] = useState(0);
  useEffect(() => { const f = () => setK((x) => x + 1); window.addEventListener("mivo:retry-route", f); return () => window.removeEventListener("mivo:retry-route", f); }, []);
  return <div key={k}><Outlet /></div>;
};

const Router = () => { useLang();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route path="/collections" element={<CollectionsIndex />} />
        <Route path="/collections/:slug" element={<CollectionPage />} />
        <Route path="/stores" element={<StoresIndex />} />
        <Route path="/stores/:slug" element={<StorePage />} />
        <Route path="/drops" element={<Drops />} />
        <Route path="/discover/:key" element={<Discover />} />
        <Route path="/about" element={<About />} />
        <Route path="/help" element={<Help />} />
        <Route path="/sellers" element={<SellerServices />} />
        <Route path="/shops" element={<ShopsDirectory />} />
        <Route path="/stores-for-sale" element={<StoresForSale />} />
        <Route path="/wholesale" element={<RequireAuth><Wholesale /></RequireAuth>} />
        <Route path="/account/deals" element={<RequireAuth><MyDeals /></RequireAuth>} />
        <Route path="/shops/:slug" element={<StorePage />} />
        <Route path="/sellers/apply" element={<RequireAuth><StoreApply /></RequireAuth>} />
        <Route path="/experience/:slug" element={<Experience />} />
        <Route path="/login" element={<Login />} />
        <Route path="/policies/:key" element={<PolicyPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/account/security" element={<RequireAuth><AccountSecurity /></RequireAuth>} />
        <Route path="/account/settings" element={<RequireAuth><AccountSettings /></RequireAuth>} />
        <Route path="/wishlist" element={<RequireAuth><Wishlist /></RequireAuth>} />
        <Route path="/track-order" element={<RequireAuth><TrackOrder /></RequireAuth>} />
        <Route path="/cards" element={<CardsExplore />} />
        <Route path="/cards/terms" element={<CardTerms />} />
        <Route path="/cards/claim/:code" element={<RequireAuth><ClaimCard /></RequireAuth>} />
        <Route path="/wallet" element={<RequireAuth><Wallet /></RequireAuth>} />
        <Route path="/wallet/missing-deposit" element={<RequireAuth><MissingDeposit /></RequireAuth>} />
        <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
        <Route path="/orders/:id" element={<RequireAuth><OrderPage /></RequireAuth>} />
        <Route path="/partner" element={<RequireAuth><PartnerPortal /></RequireAuth>} />
        <Route path="/partner/studio" element={<RequireAuth><StudioPage partner /></RequireAuth>} />
        <Route path="/moderation/reviews" element={<RequireAuth><ReviewsModeration /></RequireAuth>} />
        <Route path="/support/desk" element={<RequireAuth><SupportDesk /></RequireAuth>} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/messages" element={<RequireAuth><MessagesPage /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth role="super_admin"><AdminLayout /></RequireAuth>}>
          <Route index element={<AdminProducts />} />
          <Route path="products/:id" element={<ProductEditor />} />
          <Route path="bulk" element={<BulkReview />} />
          <Route path="studio" element={<StudioPage />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="i18n" element={<I18nAdmin />} />
          <Route path="hero" element={<HeroManager />} />
          <Route path="homepage" element={<AdminHomepage />} />
          <Route path="payments" element={<PaymentsAdmin />} />
          <Route path="coupons" element={<CouponsAdmin />} />
          <Route path="stores" element={<StoresAdmin />} />
          <Route path="users" element={<UsersAdmin />} />
          <Route path="requests" element={<CatalogRequests />} />
          <Route path="email" element={<EmailCenter />} />
          <Route path="notifications" element={<NotificationsAdmin />} />
          <Route path="security" element={<SecurityAdmin />} />
          <Route path="company" element={<CompanySettings />} />
          <Route path="launch" element={<LaunchReadiness />} />
          <Route path="shared-catalog" element={<AdminSharedCatalog />} />
          <Route path="source-hub" element={<SourceHub />} />
          <Route path="card-security" element={<AdminCardSecurity />} />
          <Route path="deals" element={<DealsAdmin />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <ScrollTop />
        <Router />
        <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#101718", border: "1px solid #263332", color: "#F6F8F7" } }} />
      </AppProvider>
    </BrowserRouter>
  );
}
